"use no memo";

import type {
  ReactZoomPanPinchContentRef,
  ReactZoomPanPinchRef,
} from "react-zoom-pan-pinch";
import { useEffect, useRef } from "react";
import {
  getMatrixTransformStyles,
  TransformComponent,
  TransformWrapper,
} from "react-zoom-pan-pinch";

import { useFloorPlanStore } from "~/stores/use-floor-plan-store";
import { useGeneralStore } from "~/stores/use-general-store";
import { useRegisterStore } from "~/stores/use-register-store";

export function FloorPlanMapView({
  children,
  onClick,
  canPan = true,
  canZoom = true,
  ref,
}: {
  children: React.ReactNode;
  onClick?: (e: React.MouseEvent<HTMLDivElement>) => void;
  canPan?: boolean;
  canZoom?: boolean;
  ref?: React.Ref<HTMLDivElement>;
}) {
  const transformRef = useRef<ReactZoomPanPinchContentRef>(null);

  const { selectedSection } = useRegisterStore();

  const {
    floorPlanZoom,
    setFloorPlanZoom,
    floorPlanPosition,
    setFloorPlanPosition,
  } = useGeneralStore();

  const { setIsPanning, isPanning, setIsZooming, isZooming } =
    useFloorPlanStore();

  const handlePanning = (event: ReactZoomPanPinchRef) => {
    const { positionX, positionY } = event.state;

    if (!isPanning && !isZooming) {
      const deltaX = Math.abs(positionX - floorPlanPosition.x);
      const deltaY = Math.abs(positionY - floorPlanPosition.y);

      // Check if movement surpasses a threshold, e.g., 5 pixels
      if (deltaX > 30 || deltaY > 30) {
        // startX.current = positionX;
        // startY.current = positionY;

        setIsPanning(true);
      }
    }
  };

  const handleZoom = (event: ReactZoomPanPinchRef) => {
    const { scale } = event.state;

    if (!isZooming && !isPanning) {
      const delta = Math.abs(scale - floorPlanZoom);

      // Check if movement surpasses a threshold, e.g., 5 pixels

      if (delta > 0.1) {
        setIsZooming(true);
      }
    }
  };

  const handlePanningStop = (event: ReactZoomPanPinchRef) => {
    // Reset panning state
    setTimeout(() => {
      // setFloorPlanPosition({
      //   x: event.state.positionX,
      //   y: event.state.positionY,
      // });
      setIsPanning(false);
    }, 100);
  };

  const handleZoomStop = (e: ReactZoomPanPinchRef) => {
    // Reset zooming state
    setTimeout(() => {
      setFloorPlanZoom(e.state.scale);
      setIsZooming(false);
    }, 100);
  };

  useEffect(() => {
    transformRef.current?.resetTransform();
  }, [selectedSection]);

  useEffect(() => {
    transformRef.current?.resetTransform();
  }, [floorPlanPosition]);

  if (!selectedSection) return;

  return (
    <TransformWrapper
      panning={{ allowRightClickPan: false, disabled: !canPan }}
      ref={transformRef}
      wheel={{ disabled: !canZoom }}
      customTransform={(x, y, scale) => getMatrixTransformStyles(x, y, scale)}
      doubleClick={{ disabled: true }}
      centerOnInit={true}
      alignmentAnimation={{
        disabled: true,
        animationTime: 0,
      }}
      zoomAnimation={{
        disabled: true,
      }}
      velocityAnimation={{
        disabled: true,
      }}
      onPanning={(event) => {
        handlePanning(event);
      }}
      onZoom={(event) => {
        handleZoom(event);
      }}
      onZoomStop={handleZoomStop}
      onPanningStop={handlePanningStop}
      initialPositionX={0}
      initialPositionY={0}
      minScale={
        canZoom ? (selectedSection.width / selectedSection.height) * 0.2 : 1
      }
      initialScale={canZoom ? floorPlanZoom : 1}
      maxScale={canZoom ? 1.8 : 1}
      limitToBounds={true}
      disablePadding={true}
    >
      {() => (
        <TransformComponent
          contentStyle={{ willChange: "transform" }}
          wrapperClass="overflow-auto !w-full !h-full hide-scrollbar"
        >
          <div
            style={{
              backgroundColor: "#080808",
              backgroundImage: `url('/patterns/charlie-brown.svg')`,
              backgroundRepeat: "repeat",
              backgroundSize: "200px",
            }}
            ref={ref}
            onClick={(e) => {
              if (onClick && !isPanning) {
                onClick(e);
              }
            }}
            role="presentation"
            data-floorplan
          >
            <div
              style={{
                width: selectedSection.width,
                height: selectedSection.height,
              }}
            >
              {children}
            </div>
          </div>
        </TransformComponent>
      )}
    </TransformWrapper>
  );
}
